// scaleUtils.js
import { scaleBand, scaleTime, scaleOrdinal } from 'd3';
import * as d3_chroma from 'd3-scale-chromatic';
import {
  getTimeRangeFromValues,
  getDateTicksModeFromOptions,
  getObjectDefTrg,
  getDateTicks,
} from '../../Utils/Utils';
import { isJSON } from '../../Utils/QueryBuilderUtils';
import { NUM_LEVEL } from '../../../constants/layout';
import {
  getFieldListResolutionCore,
  getResolvedOptionsAll,
} from '../../../hooks/useFieldListResolver';

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Get the color for an employee based on their profile
 * @param {string} objId - Employee ID
 * @param {Object} profiles - Profile data
 * @returns {string} Color code
 */
export const getEmployeeColor = (objId, profiles) => {
  if (profiles && profiles[objId]) {
    return profiles[objId].profilecolor;
  }
  return '#CCCCCC';
};

/**
 * Initialize scale bands for time data
 * @param {Array} bandValues - Values to create scale for
 * @param {Object} fieldDef - Field definition
 * @param {number} timelineShift - Timeline shift value
 * @returns {Object} Scale objects for band and color
 */
export const initializeTimeScales = (
  bandValues,
  fieldDef,
  timelineShift,
  timeLineCentralDate
) => {
  const width = 100;
  const bandValuesNoRedund = [...new Set(bandValues)];
  const options = fieldDef.options;
  const isMonth = options?.includes('month');
  const tickMode = getDateTicksModeFromOptions(fieldDef.options);

  const timeRange = getTimeRangeFromValues(
    bandValuesNoRedund,
    tickMode,
    timelineShift,
    timeLineCentralDate
  );

  const bandScale = scaleTime()
    .domain(timeRange)
    .nice()
    .range([0, width])
    .clamp(false);

  const colorScale = scaleTime()
    .domain(bandValuesNoRedund)
    .nice()
    .range(['#777777', '#777777']);

  let lastYear = null;
  const ticks =
    getDateTicks(width, 100, bandScale, tickMode).map((item) => {
      const d = new Date(parseInt(item));
      const dd = d.getDate();
      const mm = isMonth ? MONTH_SHORT[d.getMonth()] : `/${d.getMonth() + 1}`;
      const yy = (isMonth ? '' : '/') + (d.getFullYear() % 100);

      let out = '';
      if (!isMonth) out += dd;
      out += mm;
      if (yy !== lastYear) out += yy;
      lastYear = yy;
      return { raw: item, display: out, color: colorScale(item), icon: null };
    }) || [];

  return { bandScale, colorScale, ticks };
};

/**
 * Initialize scale bands for categorical data
 * @param {Array} bandValues - Values to create scale for
 * @param {Object} fieldDef - Field definition
 * @param {Object} profiles - Profile data for employee colors
 * @returns {Object} Scale objects for band and color
 */

const fallbackPalette = [
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#F7B731',
  '#9B59B6',
  '#E67E22',
  '#2ECC71',
  '#1ABC9C',
  '#3498DB',
  '#E74C3C',
];
export const initializeCategoricalScales = (
  bandValues,
  fieldDef,
  profiles,
  dicoNameRef
) => {
  const width = 100;
  const bandValuesNoRedund = [...new Set(bandValues)];
  let ticks = [];
  console.log('bandValuesNoRedund');
  console.log(fieldDef ? fieldDef.type : 'no fielddeef');
  console.log(bandValuesNoRedund);
  const bandScale = scaleBand()
    .domain(bandValuesNoRedund)
    .rangeRound([0, width], 0.1);
  let colorScale;
  if (fieldDef && fieldDef.type?.includes('emplo')) {
    colorScale = scaleOrdinal()
      .domain(bandValuesNoRedund)
      .range(
        bandValuesNoRedund.map((value) => getEmployeeColor(value, profiles))
      );
  } else if (
    fieldDef &&
    fieldDef.type === 'list' &&
    fieldDef.options &&
    isJSON(fieldDef.options)
  ) {
    ticks = getResolvedOptionsAll({ fieldDef }).map((elem) => {
      return {
        raw: elem.value,
        display: elem.display,
        color: elem.color,
        icon: elem.icon,
      };
    });
    let arrayOptions = JSON.parse(fieldDef.options);
    colorScale = scaleOrdinal()
      .domain(bandValuesNoRedund)
      .range(
        bandValuesNoRedund.map((value, index) => {
          const color = arrayOptions.find(
            (item) => item.value === value
          )?.color;
          return color ?? fallbackPalette[index % fallbackPalette.length];
        })
      );
  } else {
    colorScale = scaleOrdinal()
      .domain(bandValuesNoRedund)
      .range(['#1971C2', ...d3_chroma.schemeDark2]);
  }

  if (ticks.length === 0) {
    ticks = bandValuesNoRedund.map((value, index) => {
      return {
        raw: value,
        color: colorScale(value),
        display: dicoNameRef?.[value] ?? value,
        icon: null,
      };
    });
  }
  return { bandScale, colorScale, ticks };
};

/**
 * Initialize all scale bands for the tree
 * @param {Array} bandValues - Array of band values for each level
 * @param {Array} arrayClassifBy - Classification fields
 * @param {Array} objectsDefList - Object definitions
 * @param {Object} timelineShift - Timeline shift values
 * @param {Object} profiles - Profile data
 * @returns {Object} Scale objects for bands and colors
 */
export const initializeAllScales = (
  bandValues,
  arrayClassifBy,
  objectsDefList,
  timelineShift,
  profiles,
  objectTypes,
  timeLineCentralDates,
  fieldsDef,
  dicoNameRef
) => {
  const bandScales = new Array(NUM_LEVEL); // Initialise avec le nbr de level autorisé
  const colorScales = new Array(NUM_LEVEL);
  const ticks = new Array(NUM_LEVEL);
  for (let indBand = 0; indBand < NUM_LEVEL; indBand++) {
    const currentBandValues = bandValues[indBand];
    const timeLineCentralDate = timeLineCentralDates[indBand];
    if (!currentBandValues) continue;

    const objectType = objectTypes[indBand];
    const fieldName = arrayClassifBy[indBand];

    const fieldDef = fieldsDef[indBand];
    // const fieldDef = getObjectDefTrg(objectsDefList, objectType, fieldName);

    let scales;
    if (fieldDef && fieldDef.type === 'date') {
      scales = initializeTimeScales(
        currentBandValues,
        fieldDef,
        timelineShift[indBand],
        timeLineCentralDate
      );
    } else {
      scales = initializeCategoricalScales(
        currentBandValues,
        fieldDef,
        profiles,
        dicoNameRef
      );
    }
    console.log('indBand:%d', indBand);
    console.log(scales.ticks);
    bandScales[indBand] = scales.bandScale;
    colorScales[indBand] = scales.colorScale;
    // bandValues[indBand] = scales.bandValuesNoRedund;
    ticks[indBand] = scales.ticks;
  }

  return { bandScales, colorScales, bandValues, ticks };
};

/**
 * Update scale ranges based on band configuration
 * @param {Array} scales - Array of scale objects
 * @param {Array} objectTypes - Array of object types
 * @param {Array} arrayClassifBy - Classification fields
 * @param {Array} objectsDefList - Object definitions
 * @returns {Array} Updated scales
 */
export const updateScaleRanges = (
  scales,
  objectTypes,
  arrayClassifBy,
  objectsDefList
) => {
  const width = 100;

  return scales.map((scale, indBand) => {
    if (!scale) return scale;

    const objectType = objectTypes[indBand];
    const fieldName = arrayClassifBy[indBand];
    const fieldDef = getObjectDefTrg(objectsDefList, objectType, fieldName);

    if (!fieldDef) return scale;

    if (fieldDef.type === 'date') {
      return scale.range([0, width]).clamp(false);
    }

    return scale.rangeRound([0, width], 0.1);
  });
};
